import type { RichText } from "@storytell/cp-client";
import { SolidStreamingMarkdown } from "@storytell/markdown";
import { proseMirrorToRichText } from "@storytell/rich-text";
import type { StreamingBlock } from "@storytell/streaming";
import { StButton } from "@storytell/ui/buttons";
import { TbRefresh } from "solid-icons/tb";
import { Show } from "solid-js";
import { LogoNoText } from "~/components/brand";
import { useLanguageRegion } from "~/hooks/useLanguageRegion";
import { useWire } from "~/wire";
import { createMarkdownStreamerFromDeltas } from "./internal/markdownStreamerFromDeltas";

interface InterstitialUserFacingRendererProps {
  block: StreamingBlock;
  onBlockClick?: () => void;
}

/**
 * Renders user-facing interstitial blocks - messages that are meant for
 * direct user consumption, like explanations, clarifications, or user-friendly status updates.
 * Uses streaming markdown to properly render text content with delta updates.
 */
export const InterstitialUserFacingRenderer = (props: InterstitialUserFacingRendererProps) => {
  const markdownStreamer = createMarkdownStreamerFromDeltas(() => props.block.deltas.map((delta) => delta.text));
  const hasContent = () => props.block.deltas.length > 0 || props.block.finalContent?.type === "text";
  const showRetry = () => props.block.activity === "error";
  const wire = useWire();
  const languageRegion = useLanguageRegion();

  const handleRetry = () => {
    const msg = wire.stream.message.get(props.block.messageId);
    if (!msg) return;

    const submissionContent = msg.prompt.richText
      ? proseMirrorToRichText(msg.prompt.richText)
      : ({
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: msg.prompt.text,
                },
              ],
            },
          ],
          attrs: {
            title: "",
            createdAt: BigInt(Date.now()),
            updatedAt: BigInt(Date.now()),
            version: "1.0",
          },
        } as RichText.RichText);

    const workspaceId = wire.auth.realm().workspaceId;
    const projectId = wire.auth.realm().projectId;
    const scope = wire.chat.scope.getScopeForPromptSubmit();
    const locale = languageRegion();

    wire.chat.startOrContinueChat({
      submission: submissionContent,
      locale,
      projectId,
      workspaceId,
      scope,
    });
  };

  return (
    <Show when={hasContent()}>
      <div
        data-renderer="interstitial-user-facing-renderer"
        class="relative left-[-3.7rem] bg-canvas px-4 py-6 rounded-2xl"
      >
        <div class="flex items-start gap-4">
          <div class="flex-shrink-0">
            <div class="w-6 h-6 rounded-full flex items-center justify-center">
              <LogoNoText />
            </div>
          </div>

          <div class="flex-1 min-w-0">
            <Show when={props.block.title}>
              <div class="text-base font-medium text-primary-text mb-2">{props.block.title}</div>
            </Show>
            <Show when={hasContent()}>
              <div class="text-base text-primary-text max-w-none">
                <Show
                  when={props.block.deltas.length > 0}
                  fallback={
                    <div>{props.block.finalContent?.type === "text" ? props.block.finalContent?.text : ""}</div>
                  }
                >
                  <SolidStreamingMarkdown
                    doc={markdownStreamer.doc}
                    options={{
                      classes: {
                        document: "wrap-break-word whitespace-break-spaces [&>p:last-child]:mb-0",
                        heading: (node) => {
                          switch (node.level) {
                            case 1:
                              return "font-medium text-base mb-2 mt-3";
                            case 2:
                              return "font-medium text-sm mb-2 mt-2";
                            case 3:
                              return "font-medium text-sm mb-1 mt-2";
                            default:
                              return "font-medium text-sm mb-1 mt-1";
                          }
                        },
                        paragraph: "mb-2",
                        code: "bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded text-xs",
                        codeBlock: "bg-blue-100 dark:bg-blue-900 p-3 rounded-lg text-xs font-mono overflow-x-auto mb-3",
                        list: "mb-2",
                        item: "mb-1",
                        strong: "font-semibold",
                        emph: "italic",
                        link: "text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-200",
                        blockQuote:
                          "border-l-2 border-blue-300 dark:border-blue-700 pl-3 py-1 mb-3 text-blue-700 dark:text-blue-100 italic",
                      },
                    }}
                  />
                </Show>
              </div>
            </Show>

            <Show when={!props.block.isComplete}>
              <div class="flex items-center gap-2 mt-3">
                <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span class="text-xs text-blue-600 dark:text-blue-300">Composing...</span>
              </div>
            </Show>
            <Show when={showRetry()}>
              <div class="mt-4">
                <StButton class="px-3 py-1.5" icon={TbRefresh} onClick={handleRetry}>
                  Retry Prompt
                </StButton>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  );
};
